import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, Globe, BarChart3, Copy, KeyRound } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const EMPTY_FORM = {
  nombre: '', dominio: '', telefono: '', whatsapp: '', direccion: '', email: '',
  slogan: '', horario: '', prefijo_factura: 'MF', logo_url: '',
  colores: { primary: '#3b82f6', secondary: '#1e40af', accent: '#f59e0b', background: '#0f172a', text: '#ffffff' },
  admin_email: '', admin_password: '', admin_nombre: ''
};

const PlatformAdmin = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [stats, setStats] = useState({});

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(res.data.data || []);
      // Fetch stats for each tenant
      const statsMap = {};
      for (const t of (res.data.data || [])) {
        try {
          const s = await api.get(`/tenants/${t.id}/stats`);
          statsMap[t.id] = s.data.stats;
        } catch { statsMap[t.id] = {}; }
      }
      setStats(statsMap);
    } catch {
      toast.error('Error al cargar tenants');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setFormData({
      nombre: t.nombre || '', dominio: t.dominio || '', telefono: t.telefono || '',
      whatsapp: t.whatsapp || '', direccion: t.direccion || '', email: t.email || '',
      slogan: t.slogan || '', horario: t.horario || '', prefijo_factura: t.prefijo_factura || 'MF',
      logo_url: t.logo_url || '',
      colores: t.colores || { ...EMPTY_FORM.colores }
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { admin_email, admin_password, admin_nombre, ...updateData } = formData;
        await api.put(`/tenants/${editing.id}`, updateData);
        toast.success('Empresa actualizada');
      } else {
        const res = await api.post('/tenants', formData);
        toast.success('Empresa creada');
        // Show credentials if admin was created
        if (res.data.admin_created && res.data.admin_credentials) {
          setCredentials({
            empresa: res.data.nombre,
            ...res.data.admin_credentials
          });
          setCredentialsDialog(true);
        } else if (res.data.admin_message) {
          toast.info(res.data.admin_message);
        }
      }
      fetchTenants();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar empresa "${nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/tenants/${id}`);
      toast.success('Empresa eliminada');
      fetchTenants();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const updateColor = (key, value) => {
    setFormData(prev => ({ ...prev, colores: { ...prev.colores, [key]: value } }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="platform-title">
              <Building2 className="h-8 w-8 text-indigo-600" />
              Global Metafora App
            </h1>
            <p className="text-gray-500 mt-1">Panel de administración de empresas</p>
          </div>
          <Button onClick={openCreate} data-testid="create-tenant-btn">
            <Plus className="mr-2 h-4 w-4" />Nueva Empresa
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Empresas</p>
                  <p className="text-3xl font-bold" data-testid="total-tenants">{tenants.length}</p>
                </div>
                <Building2 className="h-10 w-10 text-indigo-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Empresas Activas</p>
                  <p className="text-3xl font-bold text-green-600">{tenants.filter(t => t.activo).length}</p>
                </div>
                <Globe className="h-10 w-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Usuarios</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {Object.values(stats).reduce((sum, s) => sum + (s.usuarios || 0), 0)}
                  </p>
                </div>
                <BarChart3 className="h-10 w-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tenants Table */}
        <Card>
          <CardHeader><CardTitle>Empresas Registradas</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-8">Cargando...</div> : (
              <Table data-testid="tenants-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Dominio</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Usuarios</TableHead>
                    <TableHead>Envíos</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center">No hay empresas registradas</TableCell></TableRow>
                  ) : tenants.map((t) => (
                    <TableRow key={t.id} data-testid={`tenant-row-${t.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full" style={{ backgroundColor: t.colores?.primary || '#3b82f6' }} />
                          <div>
                            <p className="font-semibold">{t.nombre}</p>
                            <p className="text-xs text-gray-400">{t.prefijo_factura}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-mono">{t.dominio}</TableCell>
                      <TableCell className="text-sm">{t.telefono || '-'}</TableCell>
                      <TableCell className="text-center">{stats[t.id]?.usuarios || 0}</TableCell>
                      <TableCell className="text-center">{stats[t.id]?.envios || 0}</TableCell>
                      <TableCell>
                        <Badge variant={t.activo ? 'default' : 'secondary'}>{t.activo ? 'Activa' : 'Inactiva'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`}>
                            <Edit className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id, t.nombre)} data-testid={`delete-tenant-${t.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="tenant-form">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nombre de Empresa *</Label><Input data-testid="tenant-nombre" value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} required /></div>
              <div><Label>Dominio *</Label><Input data-testid="tenant-dominio" value={formData.dominio} onChange={e => setFormData({...formData, dominio: e.target.value})} placeholder="ejemplo.com" required /></div>
              <div><Label>Teléfono</Label><Input value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} /></div>
              <div><Label>WhatsApp</Label><Input value={formData.whatsapp} onChange={e => setFormData({...formData, whatsapp: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
              <div><Label>Prefijo Factura</Label><Input value={formData.prefijo_factura} onChange={e => setFormData({...formData, prefijo_factura: e.target.value})} /></div>
              <div className="col-span-2"><Label>Dirección</Label><Input value={formData.direccion} onChange={e => setFormData({...formData, direccion: e.target.value})} /></div>
              <div><Label>Slogan / Subtítulo</Label><Input value={formData.slogan} onChange={e => setFormData({...formData, slogan: e.target.value})} /></div>
              <div><Label>Horario</Label><Input value={formData.horario} onChange={e => setFormData({...formData, horario: e.target.value})} placeholder="Lun-Sab 8:00 AM - 6:00 PM" /></div>
              <div className="col-span-2"><Label>URL del Logo</Label><Input value={formData.logo_url} onChange={e => setFormData({...formData, logo_url: e.target.value})} placeholder="/api/static/logo.png" /></div>
            </div>

            {/* Colors */}
            <div>
              <Label className="text-base font-semibold">Colores de Marca</Label>
              <div className="grid grid-cols-5 gap-3 mt-2">
                {[
                  { key: 'primary', label: 'Primario' },
                  { key: 'secondary', label: 'Secundario' },
                  { key: 'accent', label: 'Acento' },
                  { key: 'background', label: 'Fondo' },
                  { key: 'text', label: 'Texto' },
                ].map(c => (
                  <div key={c.key} className="text-center">
                    <input type="color" value={formData.colores[c.key]} onChange={e => updateColor(c.key, e.target.value)} className="w-full h-10 cursor-pointer rounded border" />
                    <p className="text-xs text-gray-500 mt-1">{c.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="rounded-lg p-4 border" style={{ backgroundColor: formData.colores.background, color: formData.colores.text }}>
              <p className="text-xs opacity-60 mb-1">Vista previa</p>
              <h3 className="text-lg font-bold">{formData.nombre || 'Nombre de la Empresa'}</h3>
              <p className="text-sm opacity-80">{formData.slogan || 'Slogan aquí'}</p>
              <div className="flex gap-2 mt-2">
                <span className="px-3 py-1 rounded text-sm text-white" style={{ backgroundColor: formData.colores.primary }}>Primario</span>
                <span className="px-3 py-1 rounded text-sm text-white" style={{ backgroundColor: formData.colores.accent }}>Acento</span>
              </div>
            </div>

            {/* Admin Section - only for new tenants */}
            {!editing && (
              <div className="border-t pt-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <KeyRound className="h-4 w-4" />
                  Administrador de la Empresa
                </Label>
                <p className="text-xs text-gray-500 mb-3">Dejar vacío para generar automáticamente</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Nombre del Admin</Label><Input data-testid="admin-nombre" value={formData.admin_nombre} onChange={e => setFormData({...formData, admin_nombre: e.target.value})} placeholder={`Admin ${formData.nombre || 'Empresa'}`} /></div>
                  <div><Label>Email del Admin</Label><Input data-testid="admin-email" type="email" value={formData.admin_email} onChange={e => setFormData({...formData, admin_email: e.target.value})} placeholder={`admin@${formData.dominio || 'dominio.com'}`} /></div>
                  <div className="col-span-2"><Label>Contraseña</Label><Input data-testid="admin-password" value={formData.admin_password} onChange={e => setFormData({...formData, admin_password: e.target.value})} placeholder="Se genera automáticamente si se deja vacío" /></div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="tenant-submit">{editing ? 'Guardar Cambios' : 'Crear Empresa'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={credentialsDialog} onOpenChange={setCredentialsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <KeyRound className="h-5 w-5" />
              Empresa Creada Exitosamente
            </DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="space-y-4" data-testid="credentials-dialog">
              <p className="text-sm text-gray-600">Se creó la empresa <strong>{credentials.empresa}</strong> con las siguientes credenciales de administrador:</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 border">
                <div className="flex justify-between items-center">
                  <div><Label className="text-xs text-gray-500">Nombre</Label><p className="font-medium">{credentials.nombre}</p></div>
                </div>
                <div className="flex justify-between items-center">
                  <div><Label className="text-xs text-gray-500">Email</Label><p className="font-mono font-medium" data-testid="cred-email">{credentials.email}</p></div>
                  <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(credentials.email); toast.success('Email copiado'); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex justify-between items-center">
                  <div><Label className="text-xs text-gray-500">Contraseña</Label><p className="font-mono font-medium" data-testid="cred-password">{credentials.password}</p></div>
                  <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(credentials.password); toast.success('Contraseña copiada'); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-amber-600 font-medium">Guarde estas credenciales. La contraseña no se podrá recuperar después de cerrar este diálogo.</p>
              <Button className="w-full" onClick={() => setCredentialsDialog(false)} data-testid="close-credentials">Entendido</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdmin;
